<script setup>
import { computed } from 'vue'
import { useQueueStore } from '@state/stores/queue'
import { usePlaybackStore } from '@state/stores/playback'
import ShowService from './../services/show-service'
import modalService from '@/services/modal-service'
import { useAppearanceStore } from '@state/stores/appearance'
import { useAuthStore } from '@state/stores/auth'
import { useShowsStore } from '@state/stores/shows'
import { useQueueService } from '@composables/use-queue-service'

const props = defineProps({
    resource: {
        type: [Object, Array],
        default: () => {},
    },
    icon: {
        type: Boolean,
        default: false,
    },
    /**
     * Use circular icon button style (new design)
     */
    circular: {
        type: Boolean,
        default: false,
    },
    /**
     * Size of the circular button: 'small' (40px), 'medium' (56px), 'large' (65px)
     */
    size: {
        type: String,
        default: 'medium',
        validator: value => ['small', 'medium', 'large'].includes(value),
    },
    type: {
        type: String,
        default: 'primary', // tag, primary, primaryFlat, secondary, transparent, tagSecondary, facebook, highlight, tagBlack, black, nadir, blue, follow
    },
    playlist: {
        type: Object,
        default: () => {},
    },
})

const appearanceStore = useAppearanceStore()
const authStore = useAuthStore()
const queueStore = useQueueStore()
const playbackStore = usePlaybackStore()
const queueService = useQueueService()

const playback = computed(() => playbackStore)

const loggedIn = computed(() => authStore.loggedIn)

const playing = computed(
    () =>
        (playback.value.status === 'playing' || playback.value.status === 'loading') &&
        ((playback.value.resource &&
            props.resource &&
            playback.value.resource.type === props.resource.type &&
            playback.value.resource.id === props.resource.id) ||
            (props.playlist && queueStore.playlistId === props.playlist.id))
)

const playIcon = computed(() => (playing.value ? 'pause' : 'play'))

const currentIcon = computed(() => (props.icon ? playIcon.value : ''))

const isMixLoaded = computed(
    () => playbackStore.resource && props.resource.id === playbackStore.resource.id
)

const buttonTitle = computed(() => (playing.value ? 'Pause' : 'Play'))

function increaseReach(id) {
    // const id = this.resource.id

    ShowService().increaseSessionReach(id)
    useShowsStore().increaseReach()
}

function handleClick() {
    if (props.playlist) return

    if (isMixLoaded.value) return queueService.pause()

    if (playing.value) {
        queueService.pause()
    } else if (props.resource && props.resource.type && props.resource.type === 'channel') {
        if (!loggedIn.value) {
            modalService.show('AUTH_REQUIRED_FOR_PLAYBACK')
            return
        }
        queueService.playRadioChannel(props.resource.id).catch(e => {
            appearanceStore.handleNotification({ message: e.message, type: 'warning' })
        })
    } else {
        if (!loggedIn.value) {
            modalService.show('AUTH_REQUIRED_FOR_PLAYBACK')
            return
        }
        queueService
            .playMix(props.resource)
            .then(data => {
                increaseReach(props.resource.show_id.id)
            })
            .catch(e => {
                appearanceStore.handleNotification({ message: e.message, type: 'warning' })
            })
    }
}
</script>

<template>
    <button
        v-if="circular"
        :class="[
            'circular-button',
            size,
            (resource && resource.id) || (playlist && playlist.id) ? '' : 'inactive',
        ]"
        :title="buttonTitle"
        :aria-label="buttonTitle"
        @click="handleClick"
    >
        <span class="disc">
            <BaseIcon
                :name="playIcon"
                :size="24"
            />
        </span>
    </button>
    <BaseButton
        v-else
        :icon="currentIcon"
        :button-type="type"
        :class="[(resource && resource.id) || (playlist && playlist.id) ? '' : 'inactive']"
        @click="handleClick"
    >
        <slot>
            <span :title="buttonTitle">
                {{ buttonTitle }}
            </span>
        </slot>
    </BaseButton>
</template>

<style lang="scss" scoped>
@import '@design';

.inactive {
    pointer-events: none;
    opacity: 0.3;
    filter: grayscale(1);
}

.circular-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    color: $c-accent-default;
    cursor: pointer;
    background: transparent;
    border: none;
    border-radius: 50%;

    @include transition(opacity, fast);
}

.disc {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: $c-text-on-accent;
    background: $c-accent-default;
    border-radius: 50%;
}

// Size variants
.small {
    width: 40px;
    height: 40px;
}

.medium {
    width: 56px;
    height: 56px;
}

.large {
    width: 65px;
    height: 65px;
}
</style>
